import { createContext, useContext, useState, type ReactNode } from 'react';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useHealthReadinessQuery, useInvalidateHealthReadiness } from '@/lib/api/health-readiness';
import {
  capturedSnapshotNotice,
  useCanLeadHealthReadiness,
  useCaptureReadinessSnapshotMutation,
} from '@/lib/api/health-readiness-report';
import { HealthReadinessHeader } from './header';
import { headerMeta, type HrTab } from './model';

/**
 * The Health & Readiness frame (T-0242 / E22-F01): the header with its
 * meta line and actions, the tab strip, then the tab. It reads the
 * composite once (every tab reads the same cached query) and shows the tab
 * only once that read has answered, so a tab never draws half its data.
 *
 * Capture snapshot is the readiness capture the Snapshots & trend tab uses,
 * so the scorecard is frozen with it and the trend gets the point (T-0224).
 */
/**
 * The header's Capture snapshot, for a tab that offers it too (the
 * Snapshots & trend tab's "Capture the first snapshot"), so both run the
 * one capture and its notice shows in one place. `capture` is undefined
 * for a user the API would refuse (below engagement lead).
 */
export interface HrCapture {
  capture?: () => void;
  capturing: boolean;
}

const CaptureContext = createContext<HrCapture>({ capturing: false });

export function useHrCapture(): HrCapture {
  return useContext(CaptureContext);
}

export interface HealthReadinessLayoutProps {
  engagementId: string;
  active: HrTab;
  children: ReactNode;
}

export function HealthReadinessLayout({
  engagementId,
  active,
  children,
}: HealthReadinessLayoutProps) {
  const query = useHealthReadinessQuery(engagementId);
  // The capture's API floor is engagement lead.
  const canCapture = useCanLeadHealthReadiness();
  const capture = useCaptureReadinessSnapshotMutation(engagementId);
  const invalidate = useInvalidateHealthReadiness(engagementId);
  const [notice, setNotice] = useState<string | null>(null);

  const captureSnapshot = () => {
    if (capture.isPending) return;
    setNotice(null);
    capture.mutate(undefined, {
      onSuccess: (snap) => {
        setNotice(capturedSnapshotNotice(snap));
        // The meta line's "last snapshot" and every chart's trend come from
        // the composite read.
        invalidate();
      },
    });
  };

  const view = query.data;
  return (
    <div className="flex flex-col gap-4" data-testid="health-readiness">
      <HealthReadinessHeader
        engagementId={engagementId}
        active={active}
        meta={view ? headerMeta(view) : null}
        onCaptureSnapshot={canCapture ? captureSnapshot : undefined}
        capturing={capture.isPending}
        status={
          capture.error ? <ErrorMessage error={capture.error} /> : notice ? notice : undefined
        }
      />
      {query.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading health and readiness…
        </div>
      ) : query.error ? (
        <ErrorMessage error={query.error} />
      ) : !view ? (
        <EmptyState
          title="Nothing to show yet"
          description="Turn on Health Check or Go-Live Readiness for this engagement."
        />
      ) : (
        <CaptureContext.Provider
          value={{
            capture: canCapture ? captureSnapshot : undefined,
            capturing: capture.isPending,
          }}
        >
          {children}
        </CaptureContext.Provider>
      )}
    </div>
  );
}
